import React, { useEffect, useState } from 'react';
import { RefreshCw, CheckCircle, XCircle, Ban, Mail, Phone, Calendar, BookOpen, Pencil } from 'lucide-react';
import { superuserService } from '../../services/api';

interface Locataire {
  id: number;
  email: string;
  nom: string;
  prenom: string;
  telephone: string | null;
  status: string;
  created_at: string;
  bookings_count: number;
}

const LocatairesPage: React.FC = () => {
  const [locataires, setLocataires] = useState<Locataire[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editModal, setEditModal] = useState<{ open: boolean; locataire: Locataire | null }>({
    open: false,
    locataire: null
  });
  const [editForm, setEditForm] = useState({ nom: '', prenom: '', email: '', telephone: '' });

  const fetchLocataires = async () => {
    setLoading(true);
    try {
      const response = await superuserService.getLocataires();
      if (response.data?.locataires) {
        setLocataires(response.data.locataires);
      }
    } catch (error: any) {
      console.error('Erreur chargement locataires:', error.response?.data || error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocataires();
  }, []);

  const handleSuspend = async (locataireId: number) => {
    if (!window.confirm('Voulez-vous vraiment suspendre ce locataire ?')) return;
    setActionLoading(locataireId);
    try {
      await superuserService.suspendUser(locataireId);
      await fetchLocataires();
    } catch (error: any) {
      console.error('Erreur suspension:', error.response?.data || error);
      alert(error.response?.data?.error || 'Erreur lors de la suspension');
    } finally {
      setActionLoading(null);
    }
  };

  const handleActivate = async (locataireId: number) => {
    setActionLoading(locataireId);
    try {
      await superuserService.activateUser(locataireId);
      await fetchLocataires();
    } catch (error: any) {
      console.error('Erreur activation:', error.response?.data || error);
      alert(error.response?.data?.error || 'Erreur lors de l\'activation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleValidate = async (locataireId: number) => {
    if (!window.confirm('Voulez-vous valider manuellement ce locataire ? (Il pourra se connecter sans avoir validé son email)')) return;
    setActionLoading(locataireId);
    try {
      await superuserService.validateLocataire(locataireId);
      await fetchLocataires();
    } catch (error: any) {
      console.error('Erreur validation:', error.response?.data || error);
      alert(error.response?.data?.error || 'Erreur lors de la validation');
    } finally {
      setActionLoading(null);
    }
  };

  const openEditModal = (locataire: Locataire) => {
    setEditForm({
      nom: locataire.nom,
      prenom: locataire.prenom,
      email: locataire.email,
      telephone: locataire.telephone || ''
    });
    setEditModal({ open: true, locataire });
  };

  const handleUpdateLocataire = async () => {
    if (!editModal.locataire) return;
    setActionLoading(editModal.locataire.id);
    try {
      await superuserService.updateUser(editModal.locataire.id, editForm);
      setEditModal({ open: false, locataire: null });
      await fetchLocataires();
    } catch (error: any) {
      console.error('Erreur mise à jour:', error.response?.data || error);
      alert(error.response?.data?.error || 'Erreur lors de la mise à jour');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'validated':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle className="w-3 h-3" /> Actif</span>;
      case 'suspended':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"><XCircle className="w-3 h-3" /> Suspendu</span>;
      case 'pending':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Email non validé</span>;
      default:
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredLocataires = locataires.filter(loc => {
    const search = searchTerm.toLowerCase();
    return (
      loc.nom.toLowerCase().includes(search) ||
      loc.prenom.toLowerCase().includes(search) ||
      loc.email.toLowerCase().includes(search) ||
      (loc.telephone && loc.telephone.includes(search))
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-heading font-bold text-neutral-900">Liste des Locataires</h1>
          <p className="text-neutral-600">{locataires.length} locataire{locataires.length > 1 ? 's' : ''} inscrit{locataires.length > 1 ? 's' : ''}</p>
        </div>
        <button onClick={fetchLocataires} className="btn btn-outline flex items-center gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Recherche */}
      <div className="card p-4">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Rechercher par nom, email ou téléphone..."
          className="w-full border border-gray-300 rounded-lg p-3"
        />
      </div>

      {/* Liste des locataires */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      ) : filteredLocataires.length === 0 ? (
        <div className="card p-12 text-center">
          <p className="text-neutral-500">
            {searchTerm ? 'Aucun locataire trouvé pour cette recherche' : 'Aucun locataire inscrit'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredLocataires.map((locataire) => (
            <div key={locataire.id} className="card p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-lg">{locataire.prenom} {locataire.nom}</h3>
                    {getStatusBadge(locataire.status)}
                  </div>
                  <div className="space-y-1 text-sm text-neutral-600">
                    <p className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {locataire.email}
                    </p>
                    {locataire.telephone && (
                      <p className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        {locataire.telephone}
                      </p>
                    )}
                    <p className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4" />
                      {locataire.bookings_count} réservation{locataire.bookings_count > 1 ? 's' : ''}
                    </p>
                    <p className="flex items-center gap-2 text-xs text-neutral-400">
                      <Calendar className="w-3 h-3" />
                      Inscrit le {formatDate(locataire.created_at)}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => openEditModal(locataire)}
                    className="btn bg-blue-100 text-blue-700 hover:bg-blue-200 flex items-center gap-2"
                  >
                    <Pencil className="w-4 h-4" />
                    Modifier
                  </button>
                  {locataire.status === 'validated' && (
                    <button
                      onClick={() => handleSuspend(locataire.id)}
                      disabled={actionLoading === locataire.id}
                      className="btn bg-red-100 text-red-700 hover:bg-red-200 flex items-center gap-2"
                    >
                      {actionLoading === locataire.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Ban className="w-4 h-4" />
                      )}
                      Suspendre
                    </button>
                  )}
                  {locataire.status === 'suspended' && (
                    <button
                      onClick={() => handleActivate(locataire.id)}
                      disabled={actionLoading === locataire.id}
                      className="btn bg-green-100 text-green-700 hover:bg-green-200 flex items-center gap-2"
                    >
                      {actionLoading === locataire.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4" />
                      )}
                      Réactiver
                    </button>
                  )}
                  {locataire.status === 'pending' && (
                    <button
                      onClick={() => handleValidate(locataire.id)}
                      disabled={actionLoading === locataire.id}
                      className="btn bg-blue-100 text-blue-700 hover:bg-blue-200 flex items-center gap-2"
                    >
                      {actionLoading === locataire.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4" />
                      )}
                      Valider manuellement
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal d'édition */}
      {editModal.open && editModal.locataire && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Modifier le locataire</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                <input
                  type="text"
                  value={editForm.prenom}
                  onChange={(e) => setEditForm({ ...editForm, prenom: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg p-3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input
                  type="text"
                  value={editForm.nom}
                  onChange={(e) => setEditForm({ ...editForm, nom: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg p-3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg p-3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={editForm.telephone}
                  onChange={(e) => setEditForm({ ...editForm, telephone: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg p-3"
                />
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setEditModal({ open: false, locataire: null })}
                className="btn btn-outline"
              >
                Annuler
              </button>
              <button
                onClick={handleUpdateLocataire}
                disabled={actionLoading !== null}
                className="btn btn-primary"
              >
                {actionLoading ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocatairesPage;
